import { computed, effect, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Attachment, Channel, ChannelMemberResponse } from 'stream-chat';
import { firstValueFrom, from, take } from 'rxjs';
import { EditingMessage, ImageAttachment, QuotedMessage } from '../../../../application/interfaces';
import { MessageContextStorage } from '../../../../application/context-storage';
import {
  STREAM_CHANNEL_TOKEN,
  STREAM_CHAT_CLIENT_TOKEN,
  StreamChannelProvider,
  StreamChatClientProvider
} from '@messages/infra';
import { MentionMember } from '../mention-autocomplete/mention-autocomplete.component';
import { ChatViewService } from '../../view-service/chat.view-service';

export class MessageInputViewService {
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);
  private readonly messageContextStorage: MessageContextStorage = inject(MessageContextStorage);
  private readonly chatViewService: ChatViewService = inject(ChatViewService);

  readonly messageText: WritableSignal<string> = signal<string>('');
  readonly isSending: WritableSignal<boolean> = signal<boolean>(false);
  readonly selectedImage: WritableSignal<File | null> = signal<File | null>(null);
  readonly imagePreview: WritableSignal<string | null> = signal<string | null>(null);

  readonly showMentionAutocomplete: WritableSignal<boolean> = signal<boolean>(false);
  readonly mentionFilterText: WritableSignal<string> = signal<string>('');
  readonly mentionedUserIds: WritableSignal<string[]> = signal<string[]>([]);
  private mentionStartIndex: number = -1;

  readonly editingMessage: Signal<EditingMessage | null> = toSignal(this.messageContextStorage.editingMessage$, {
    initialValue: null
  });

  readonly quotedMessage: Signal<QuotedMessage | null> = toSignal(this.messageContextStorage.quotedMessage$, {
    initialValue: null
  });

  readonly isEditing: Signal<boolean> = computed(() => this.editingMessage() !== null);

  readonly canSend: Signal<boolean> = computed(
    () => (this.messageText().trim().length > 0 || this.selectedImage() !== null) && !this.isSending()
  );

  constructor() {
    this.setupEditingWatcher();
  }

  async sendMessage(onComplete: () => void): Promise<void> {
    const text: string = this.messageText().trim();
    const image: File | null = this.selectedImage();
    const editing: EditingMessage | null = this.editingMessage();

    if ((!text && !image) || this.isSending()) return;

    this.isSending.set(true);

    try {
      if (editing) {
        this.updateMessage(editing.id, text, onComplete);
        return;
      }

      const attachments: ImageAttachment[] = [];

      if (image) {
        const imageUrl: string | null = await this.uploadImage(image);
        if (imageUrl) {
          attachments.push({
            type: 'image',
            image_url: imageUrl,
            fallback: image.name
          });
        }
      }

      const quoted: QuotedMessage | null = this.quotedMessage();
      const userIds: string[] = this.mentionedUserIds();

      if (this.chatViewService.isPending()) {
        await firstValueFrom(this.chatViewService.promotePendingToReal());
      }

      await this.channelProvider.sendMessage({
        text: text || undefined,
        attachments: attachments.length > 0 ? (attachments as Attachment[]) : undefined,
        quotedMessageId: quoted?.id,
        mentionedUserIds: userIds.length > 0 ? userIds : undefined
      });

      if (quoted?.id) {
        this.messageContextStorage.clearQuotedMessage();
      }

      this.messageText.set('');
      this.mentionedUserIds.set([]);
      this.closeMentionAutocomplete();
      this.removeSelectedImage();
      onComplete();
    } finally {
      this.isSending.set(false);
    }
  }

  selectImage(file: File): void {
    this.selectedImage.set(file);
    this.createImagePreview(file);
  }

  removeSelectedImage(): void {
    this.selectedImage.set(null);
    this.imagePreview.set(null);
  }

  cancelQuote(): void {
    this.messageContextStorage.clearQuotedMessage();
  }

  cancelEdit(onComplete: () => void): void {
    this.messageContextStorage.clearEditingMessage();
    this.messageText.set('');
    onComplete();
  }

  getChannelMembers(): MentionMember[] {
    const channel: Channel | undefined = this.channelProvider.getActiveChannel();
    if (!channel?.state?.members) return [];

    const currentUserId: string | undefined = this.chatClientProvider.getCurrentUserId();
    const members: Record<string, ChannelMemberResponse> = channel.state.members;

    return Object.values(members)
      .filter((m: ChannelMemberResponse) => m.user_id !== currentUserId && m.user)
      .map((m: ChannelMemberResponse) => ({
        id: m.user_id || m.user!.id,
        name: (m.user!.name as string) || m.user!.id || '',
        image: (m.user!.image as string) || null
      }));
  }

  handleMentionInput(text: string, cursorPosition: number): void {
    const textBeforeCursor: string = text.substring(0, cursorPosition);
    const lastAtIndex: number = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.closeMentionAutocomplete();
      return;
    }

    const charBefore: string | undefined = textBeforeCursor[lastAtIndex - 1];
    if (lastAtIndex > 0 && charBefore !== ' ' && charBefore !== '\n') {
      this.closeMentionAutocomplete();
      return;
    }

    const query: string = textBeforeCursor.substring(lastAtIndex + 1);

    this.mentionStartIndex = lastAtIndex;
    this.mentionFilterText.set(query);
    this.showMentionAutocomplete.set(true);
  }

  insertMention(
    member: MentionMember,
    currentText: string,
    cursorPosition: number
  ): { text: string; newCursorPosition: number } {
    const before: string = currentText.substring(0, this.mentionStartIndex);
    const after: string = currentText.substring(cursorPosition);
    const mentionText: string = `@${member.name} `;
    const newText: string = before + mentionText + after;
    const newCursorPosition: number = before.length + mentionText.length;

    this.mentionedUserIds.update((ids: string[]) => (ids.includes(member.id) ? ids : [...ids, member.id]));

    this.closeMentionAutocomplete();
    return { text: newText, newCursorPosition };
  }

  closeMentionAutocomplete(): void {
    this.showMentionAutocomplete.set(false);
    this.mentionFilterText.set('');
    this.mentionStartIndex = -1;
  }

  private updateMessage(messageId: string, newText: string, onComplete: () => void): void {
    from(this.chatClientProvider.updateMessage(messageId, newText))
      .pipe(take(1))
      .subscribe(() => {
        this.messageContextStorage.clearEditingMessage();
        this.messageText.set('');
        this.isSending.set(false);
        onComplete();
      });
  }

  private async uploadImage(file: File): Promise<string | null> {
    const response: { file: string } = await this.channelProvider.sendImage(file);
    return response.file || null;
  }

  private createImagePreview(file: File): void {
    const reader: FileReader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>): void => {
      this.imagePreview.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  private setupEditingWatcher(): void {
    effect(() => {
      const editing: EditingMessage | null = this.editingMessage();
      if (editing) {
        this.messageText.set(editing.text);
      }
    });
  }
}
